import { useCallback, useEffect, useState } from 'react';
import { driveDownloadUrl, driveRoot, driveStatus, driveView } from '../../brandingMarketing/driveApi';
import { getFallbackThumbDataUrl } from '../utils/thumbUtils';

const ROOT_TYPE = 'studio';
const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder';
const GOOGLE_APPS_MIME_PREFIX = 'application/vnd.google-apps.';

const isSupportedStudioAssetMimeType = (mimeType = '') => {
  const normalized = String(mimeType || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === DRIVE_FOLDER_MIME) return false;
  if (normalized.startsWith(GOOGLE_APPS_MIME_PREFIX)) return false;
  if (normalized === 'application/pdf') return true;
  if (normalized === 'image/svg+xml') return true;
  return normalized.startsWith('image/');
};

const mapDriveItem = (item, downloadUrl, thumbUrl) => {
  const mimeType = item?.mimeType || '';
  const isPdf = mimeType === 'application/pdf';
  const isSvg = mimeType === 'image/svg+xml';
  const isImage = mimeType.startsWith('image/');
  const type = isPdf ? 'pdf' : isSvg ? 'svg' : isImage ? 'image' : 'image';
  const resolvedThumb = thumbUrl || item.thumbnailLink || getFallbackThumbDataUrl(type);
  return {
    id: item.id,
    type,
    originalName: item.name || 'Upload',
    mimeType,
    thumbUrl: resolvedThumb,
    downloadUrl,
    webViewLink: item.webViewLink || '',
    bytes: Number(item.size) || 0,
    width: item?.imageMediaMetadata?.width
      ? Number(item.imageMediaMetadata.width)
      : null,
    height: item?.imageMediaMetadata?.height
      ? Number(item.imageMediaMetadata.height)
      : null,
    status: 'ready',
  };
};

const toStudioAssets = async (firebase, items = []) => {
  const supportedItems = (items || []).filter((item) => {
    if (!item?.id) return false;
    return isSupportedStudioAssetMimeType(item.mimeType);
  });

  return Promise.all(
    supportedItems.map(async (item) => {
      let downloadUrl = '';
      try {
        downloadUrl = await driveDownloadUrl(firebase, item.id, {
          rootType: ROOT_TYPE,
          forceRefresh: true,
        });
      } catch (_error) {
        downloadUrl = '';
      }
      return mapDriveItem(item, downloadUrl, item.thumbnailLink || downloadUrl);
    }),
  );
};

const useStudioAssets = (firebase) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [rootFolderId, setRootFolderId] = useState(null);

  const refresh = useCallback(async () => {
    if (!firebase || !connected || !rootFolderId) return;
    setLoading(true);
    setError('');
    try {
      const data = await driveView(firebase, rootFolderId, {}, { rootType: ROOT_TYPE });
      const items = data?.items || [];
      const withUrls = await toStudioAssets(firebase, items);
      setAssets(withUrls);
      setNeedsReconnect(false);
    } catch (err) {
      console.error('Failed to load studio Drive assets:', err);
      setError(err?.message || 'Unable to load uploads.');
      if (err?.reconnect) {
        setNeedsReconnect(true);
      }
    } finally {
      setLoading(false);
    }
  }, [connected, firebase, rootFolderId]);

  useEffect(() => {
    let active = true;
    if (!firebase) {
      setAssets([]);
      setLoading(false);
      setError('');
      setConnected(false);
      setRootFolderId(null);
      return () => {};
    }

    const init = async () => {
      setLoading(true);
      setError('');
      try {
        const status = await driveStatus(firebase, { rootType: ROOT_TYPE });
        if (!active) return;
        if (!status?.connected) {
          setConnected(false);
          setRootFolderId(null);
          setAssets([]);
          return;
        }
        setConnected(true);
        const rootId = await driveRoot(firebase, { rootType: ROOT_TYPE });
        if (!active) return;
        setRootFolderId(rootId);
        if (rootId) {
          const data = await driveView(firebase, rootId, {}, { rootType: ROOT_TYPE });
          const items = data?.items || [];
          const withUrls = await toStudioAssets(firebase, items);
          setAssets(withUrls);
        }
      } catch (err) {
        console.error('Failed to initialize studio Drive assets:', err);
        setError(err?.message || 'Unable to load uploads.');
        if (err?.reconnect) {
          setNeedsReconnect(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    init();
    return () => {
      active = false;
    };
  }, [firebase]);

  return {
    assets,
    loading,
    error,
    connected,
    needsReconnect,
    rootFolderId,
    refresh,
  };
};

export default useStudioAssets;
